import React, { useEffect, useState, useContext } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruck,
  faChartBar,
  faCircle,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";

import { Button } from "react-bootstrap";
import useCeo from "../../hooks/useCEO";
import { useUnidades } from "../../hooks/test/useUnidades";
import { Spinner } from "../../components/Spinner";
import { Spinner as Spin } from "reactstrap";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import ChartContainer from "../../components/ChartContainer";
import { botAperturas } from "../../helpers/bot";
import { AuthContext } from "../../context/authContext/authContext";
import { BitacoraUnidadesModal } from "../../components/customComponents/Modals/BitacoraUnidadesModal";
import ExportUnidades from "../../components/Exports";

// --- Constantes y funciones auxiliares (mantener en el mismo archivo) ---
const circleStyle = (color) => ({
  width: "20px",
  height: "20px",
  backgroundColor: color,
  borderRadius: "50%",
  display: "inline-block",
  margin: "0 5px",
});

const COLORS = ["#34d800", "#d31d1d", "#ff6100", "#a4a100"];
const colorBadger = {
  Éxitoso: "rgba(0, 194, 29, 1)",
  Alerta: "red", // Coincide con el rojo del ejemplo
  Advertencia: "rgba(239, 229, 0, 1)", // Amarillo
  Información: "rgba(0, 200, 200, 1)", // Azul cian o turquesa
};
const titleBadger = {
  Éxitoso: "Viajes terminados con éxito",
  Alerta: "Viajes con un incidente", // O "Incidentes"
  Advertencia: "Viajes en espera de una acción", // O "Pendientes"
  Información: "Situación del vehículo", // O "En Proceso"
};

// --- Inicio del Componente TableroGerencial ---
export const TableroGerencial = () => {
  useCeo({
    title: "Tablero de administración",
    description: "Página de tablero",
  });
  const { user } = useContext(AuthContext);
  const { getStatsUnidades, getStatsUnidadesBitacora } = useUnidades();
  const [negocioData, setNegocioData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [aperturas, setAperturas] = useState([]);
  const [barras, setBarras] = useState(false);
  const [denegadas, setDenegadas] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [globalConteoTipo, setGlobalConteoTipo] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedCedis, setSelectedCedis] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  useEffect(() => {
    loadNegocio();
  }, [isActive]);

  const handleOpenModal = (statusKey, cedis) => {
    setSelectedCedis(cedis);
    setSelectedStatus(statusKey);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCedis(null);
    setSelectedStatus(null);
    setShowModal(false);
  };

  const handleExport = async (nombreCedis) => {
    if (!nombreCedis) return; // evita error si llega vacío
    setLoadingExcel(true);

    try {
      const resp = await getStatsUnidadesBitacora(nombreCedis);
      if (!resp.ok) {
        return Swal.fire({
          title: "Error",
          text: resp.msg || "No se pudo obtener la información",
          icon: "error",
        });
      }

      // ======== genera Excel aquí ========
      ExportUnidades(resp.data, nombreCedis);
    } catch (err) {
      console.error("Error exportando", err);
      Swal.fire("Error", "Hubo un problema al exportar el archivo", "error");
    } finally {
      setLoadingExcel(false);
    }
  };

  function renderBusinessContent(
    negocio,
    user,
    globalConteoTipo,
    colorBadger,
    titleBadger,
    loading
  ) {
    if (!negocio) return null;
    const conteoTipoParaEsteNegocio = globalConteoTipo[negocio.nombre] || {};
    return (
      <div className="wrapper animated bg-white border-bottom shadow pb-3 mb-2 bg-body-tertiary rounded">
        {/* Primera fila para Logo y Totales */}
        <div className="row text-center py-3">
          <div className="col-6">
            {/* Logo del Negocio */}
            {negocio.negocio_id && (
              <img
                src={
                  process.env.REACT_APP_API_URL_LOCAL + // Usando LOCAL basado en código previo
                  `negocios/${negocio.negocio_id}/bg`
                }
                className="col-auto d-flex flex-column mx-auto"
                title={negocio.nombre}
                alt={negocio.nombre}
                style={{ maxWidth: "20%" }}
                width={"150"}
              />
            )}
          </div>
          {/* Sección de Totales de Vehículos */}
          {!loading && ( // Condición general de carga
            <div className="col-6 row justify-content-center align-items-center">
              <div className="col-md text-center">
                <h3 className="">
                  <strong className="text-center">{negocio.total || 0}</strong>
                </h3>
                <small>Total de unidades</small>
              </div>
            </div>
          )}

          {/* --- SECCIÓN CÓDIGO DE COLORES (CORREGIDA PARA MOSTRAR VALORES > 0) --- */}
          {!loading &&
            conteoTipoParaEsteNegocio &&
            Object.keys(conteoTipoParaEsteNegocio).length > 0 && (
              <div className="col-sm-12 row order-3 order-md-3 order-lg-3">
                {[
                  "Éxitoso",
                  "Información",
                  "Advertencia",
                  "Alerta",
                  "Comlpetado",
                ].map((key) => {
                  const value = conteoTipoParaEsteNegocio[key] || 0;
                  if (value <= 0) {
                    return null;
                  }
                  const colClass = "col-md";
                  return (
                    <div className={`${colClass} text-center`} key={key}>
                      <div className="text-center">
                        <i>
                          <h3 className="m-t-15">
                            <strong className="text-center">{value}</strong>
                          </h3>
                        </i>
                      </div>
                      <div className="text-center">
                        <FontAwesomeIcon
                          icon={faCircle}
                          style={{
                            color: colorBadger[key] || "rgba(0, 200, 200, 1)",
                          }}
                        />
                        &nbsp; &nbsp;
                        <small>{key}</small>
                      </div>
                      <div className="text-center">
                        <small className="text-muted">
                          {titleBadger[key] || "Vehículos pendientes"}
                        </small>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          {loading &&
            (!conteoTipoParaEsteNegocio ||
              Object.keys(conteoTipoParaEsteNegocio).lenght === 0) && (
              <div className="col-sm-12 text-center mt-3">
                <small className="text-muted">
                  No hay eventos recientes para este negocio.
                </small>
              </div>
            )}
        </div>

        <div className="row">
          {negocio &&
          negocio.cedis &&
          Object.entries(negocio.cedis).length > 0 ? (
            <>
              {" "}
              {/* Abre el fragmento que envuelve el resultado del map */}
              {Object.entries(negocio.cedis)
                .filter(([nombreCedis]) => {
                  if (user && user.roles_id === 2 && user.cedis) {
                    return user.cedis.some(
                      (cedi) => cedi && cedi.nombre_cedis === nombreCedis
                    );
                  }
                  return true;
                })
                .map(([nombreCedis, datosCedis]) => (
                  // Contenido para CADA CEDIS - envuelto en un div por item
                  <div className="col-md-6" key={nombreCedis}>
                    <div className="mb-2">
                      <div className="ibox-content product-box shadow bg-body-tertiary rounded">
                        <div className="product-desc ">
                          <h3>
                            {loadingExcel ? (
                              <Spin
                                className="col-2"
                                style={{ color: "green" }}
                              ></Spin>
                            ) : (
                              <FontAwesomeIcon
                                onClick={() => handleExport(nombreCedis)}
                                title={`Exportar unidades de ${nombreCedis}`}
                                className="col-2 cursor"
                                style={{ color: "green" }}
                                icon={faFileExport}
                              />
                            )}
                            <strong className="col-10">{nombreCedis}</strong>
                          </h3>
                          <hr />
                          <div
                            className="row"
                            style={{ overflowY: "auto", height: "7rem" }}
                          >
                            {/* Status Section */}
                            <div className="col-6">
                              <div>
                                &nbsp; &nbsp;
                                <FontAwesomeIcon
                                  icon={faTruck}
                                  className="col-auto fa-1x text-secondary"
                                  title="Total de unidades"
                                />
                                &nbsp; Estatus de las unidades:
                              </div>
                              {datosCedis &&
                              datosCedis.status &&
                              Object.entries(datosCedis.status).length > 0 ? (
                                Object.entries(datosCedis.status).map(
                                  ([statusKey, statusValue]) => (
                                    <div
                                      className="row col-12 small m-t-xs"
                                      key={statusKey}
                                      style={
                                        statusKey === "UNIDAD EN RUTA"
                                          ? {
                                              color: "green",
                                              cursor: "pointer",
                                            }
                                          : statusKey === "UNIDAD DISPONIBLE"
                                          ? { color: "blue", cursor: "pointer" }
                                          : {}
                                      }
                                      title={
                                        statusKey === "UNIDAD EN RUTA"
                                          ? `Ver unidades en ruta`
                                          : statusKey === "UNIDAD DISPONIBLE"
                                          ? "Ver unidades disponibles"
                                          : ""
                                      }
                                      onClick={
                                        [
                                          "UNIDAD EN RUTA",
                                          "UNIDAD DISPONIBLE",
                                        ].includes(statusKey)
                                          ? () =>
                                              handleOpenModal(
                                                statusKey,
                                                nombreCedis
                                              )
                                          : undefined
                                      }
                                    >
                                      <div className="col-6">
                                        <strong>{statusKey}</strong>
                                      </div>
                                      <div className="col-6 text-center">
                                        {statusValue}
                                      </div>
                                    </div>
                                  )
                                )
                              ) : (
                                <div className="row col-12 small m-t-xs">
                                  <strong>SIN ESTATUS</strong>
                                </div>
                              )}
                            </div>
                            <br />
                            {/* BitacoraStatus Section */}
                            <div className="col-6">
                              <div>
                                &nbsp; &nbsp;
                                <FontAwesomeIcon
                                  icon={faChartBar}
                                  className="col-auto fa-1x text-secondary"
                                  title="Conteo por estatus"
                                />
                                &nbsp; Conteo por estatus:
                              </div>
                              {datosCedis &&
                              datosCedis.bitacoraStatus &&
                              Object.keys(datosCedis.bitacoraStatus).length >
                                0 ? (
                                Object.entries(datosCedis.bitacoraStatus).map(
                                  ([bitacoraKey, bitacoraValue]) => (
                                    <div
                                      className="row col-12 small m-t-xs"
                                      key={bitacoraKey}
                                    >
                                      <div className="col-6">
                                        <strong>{bitacoraKey}</strong>
                                      </div>
                                      <div className="col-6 text-center">
                                        {bitacoraValue}
                                      </div>
                                    </div>
                                  )
                                )
                              ) : (
                                <div className="row col-12 small m-t-xs">
                                  <strong>SIN MOVIMIENTOS</strong>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              {selectedCedis && (
                <BitacoraUnidadesModal
                  status={selectedStatus}
                  nombreCedis={selectedCedis}
                  show={showModal}
                  handleClose={handleCloseModal}
                />
              )}
            </>
          ) : (
            // Mensaje si no hay datos de CEDIS disponibles después de filtrar
            !loading &&
            negocio &&
            negocio.cedis &&
            Object.keys(negocio.cedis).length === 0 && (
              <div className="col-12 text-center">
                Sin datos de CEDIS disponibles para este negocio.
              </div>
            )
          )}
        </div>
      </div>
    );
  }

  const loadNegocio = async () => {
    setLoading(true);
    console.log("Cargando estadisticas de negocios...");
    let resp = await getStatsUnidades();
    if (resp.ok && resp.data) {
      setNegocioData(resp.data.negocios); // Accedemos al array de negocios
      setGlobalConteoTipo(resp.data.conteoTipo); //  Accedemos al array del codigo de colores
      let filteredNegocios = resp.data.negocios;
      if (user.roles_id === 2 && user.cedis) {
        const accesibleNegocioIds = new Set(
          user.cedis.map((cedi) => cedi.negocio_id)
        );
        filteredNegocios = resp.data.negocios.filter((negocio) =>
          accesibleNegocioIds.has(negocio.negocio_id)
        );
      }
      setNegocioData(filteredNegocios); // Actualiza negocioData con los negocios filtrados por rol

      let initialActiveTabId = null;
      if (filteredNegocios && filteredNegocios.length > 0) {
        // Si ya hay una pestaña activa y es válida, la mantenemos. Si no, establecemos la primera.
        if (
          activeTab &&
          filteredNegocios.some((n) => n.negocio_id === activeTab)
        ) {
          initialActiveTabId = activeTab;
        } else {
          initialActiveTabId = filteredNegocios[0].negocio_id;
        }
        setActiveTab(initialActiveTabId);
      } else {
        setActiveTab(null);
      }
      if (isActive) {
        // Solo si la pestaña principal activa es "Administración" (o si quieres que el bot siempre cargue)
        let botIdsToLoad = [];
        if (user.roles_id === 1) {
          // Admin ve todos los negocios
          botIdsToLoad = filteredNegocios.map((n) => n.negocio_id);
        } else if (user.roles_id === 2 && user.cedis) {
          // Monitorista ve sus negocios accesibles
          botIdsToLoad = Array.from(
            new Set(user.cedis.map((cedi) => cedi.negocio_id))
          );
        } else if (initialActiveTabId) {
          // Para roles que ven un solo negocio al inicio
          botIdsToLoad = [initialActiveTabId];
        }

        if (botIdsToLoad.length > 0) {
          loadBot(botIdsToLoad);
        } else {
          setAperturas([]); // No hay negocios para cargar el bot
        }
      } else {
        setAperturas([]); // Si no está en la pestaña "Administración", limpiar aperturas
      }
    } else {
      console.error(
        "Error al cargar estadísticas de negocios:",
        resp && resp.msg
      ); // Log error
      setNegocioData([]); // Resetear estados en caso de error
      setGlobalConteoTipo({});
      setActiveTab(null);
      setAperturas([]);
    }
    setLoading(false);
  };

  const loadBot = async (negocio_ids_to_load) => {
    setLoading(true);

    if (!negocio_ids_to_load || negocio_ids_to_load.length === 0) {
      setAperturas([]);
      setLoading(false);
      return;
    }

    const aperturasBot = await botAperturas(negocio_ids_to_load);
    if (!aperturasBot.ok) {
      Swal.fire({
        title: "¡Lo siento!",
        text: aperturasBot.error.msg,
        icon: "info",
      });
    } else {
      setAperturas(aperturasBot.data.data);
    }
    setLoading(false);
  };

  const valoresCedis = (participacion) => {
    const midIndex = Math.ceil(participacion.length / 2);
    const firstColumn = participacion.slice(0, midIndex);
    const secondColumn = participacion.slice(midIndex);

    return (
      <div style={{ display: "flex" }}>
        <ul style={{ flex: 1 }}>
          {firstColumn.map(({ name, value }) => (
            <li
              title={
                value
                  ? "Se esta usando el bot en este momento"
                  : "No se esta usando el bot en este momento"
              }
              className={`text-${value ? "success" : "danger"}`}
              key={name}
            >
              {name}
            </li>
          ))}
        </ul>
        <ul style={{ flex: 1 }}>
          {secondColumn.map(({ name, value }) => (
            <li
              title={
                value
                  ? "No se esta usando el bot en este momento"
                  : "Se esta usando el bot en este momento"
              }
              className={`text-${value ? "success" : "danger"}`}
              key={name}
            >
              {name}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <>
      {loading && <Spinner />}
      <Tabs
        defaultActiveKey="administracion"
        id="fill-tab-example"
        className="mb-3 p-2"
        fill
        onSelect={(key) => {
          const newIsActive = key === "administracion";
          setIsActive(newIsActive);
          // Si cambia a la pestaña "BOT Aperturas", necesitamos recargar el bot
          // con los IDs correctos para el usuario actual.
          if (
            key === "bot-aperturas" &&
            user.roles_id === 1 &&
            negocioData.length > 0
          ) {
            loadBot(negocioData.map((n) => n.negocio_id));
          } else if (
            key === "bot-aperturas" &&
            user.roles_id === 2 &&
            user.cedis
          ) {
            loadBot(
              Array.from(new Set(user.cedis.map((cedi) => cedi.negocio_id)))
            );
          } else if (key === "bot-aperturas" && activeTab) {
            loadBot([activeTab]); // Carga el bot solo para el negocio activo
          } else if (key === "bot-aperturas" && negocioData.length > 0) {
            // Fallback para un solo negocio sin activeTab
            loadBot([negocioData[0].negocio_id]);
          } else {
            setAperturas([]); // Limpia aperturas si no está en la pestaña del bot
          }
        }}
      >
        {/**Pestaña de administracion**/}
        <Tab
          eventKey="administracion"
          title="--Administración--"
          className="p-2"
        >
          {/* Caso 1: No hay datos de negocios y no está cargando */}
          {!loading && (!negocioData || negocioData.length === 0) && (
            <div className="text-center p-4">
              <h4>No hay datos de negocios disponibles para mostrar.</h4>
            </div>
          )}
          {/* Caso 2: Hay exactamente un negocio */}
          {!loading &&
            negocioData &&
            negocioData.length === 1 &&
            renderBusinessContent(
              negocioData[0],
              user,
              globalConteoTipo,
              colorBadger,
              titleBadger,
              loading
            )}
          {/* Caso 3: Hay más de un negocio */}
          {!loading && negocioData && negocioData.length > 1 && (
            <Tabs
              id="business-tabs" // ID único para las tabs de negocios
              activeKey={activeTab} // Controla qué pestaña de negocio está activa
              onSelect={(k) => {
                setActiveTab(k);
                // Si la pestaña actual es la del bot y se cambia el negocio, recarga el bot
                // con el ID del negocio seleccionado (solo si el rol no es admin)
                if (!isActive && user.roles_id !== 1) {
                  // If "BOT Aperturas" tab is active and not admin
                  loadBot([k]);
                }
              }}
              className="mb-3 p-2"
              fill
            >
              {/* Mapeamos sobre el array negocioData para crear una <Tab> por cada negocio */}
              {negocioData.map((negocio) => (
                <Tab
                  eventKey={negocio.negocio_id} // El ID del negocio como clave del evento de la pestaña
                  title={negocio.nombre} // El nombre del negocio como título de la pestaña
                  key={negocio.negocio_id} // Key para React al mapear
                >
                  {/* Dentro de cada <Tab>, llamamos a la función auxiliar renderBusinessContent
                      para el 'negocio' actual en la iteración del map */}
                  {renderBusinessContent(
                    negocio,
                    user,
                    globalConteoTipo,
                    colorBadger,
                    titleBadger,
                    loading
                  )}
                </Tab>
              ))}
            </Tabs>
          )}
        </Tab>
        <Tab
          disabled={user.roles_id !== 1 ? true : false}
          eventKey="bot-aperturas"
          title="--BOT Aperturas--"
        >
          {aperturas.length > 0 && aperturas[0].total > 0 ? (
            <div className="container-fluid">
              <div className="row">
                {!loading ? (
                  <h2 className="text-center mt-2">
                    <b>Solicitudes de Aperturas por día</b>
                  </h2>
                ) : (
                  <Spinner className="text-center mt-2" />
                )}
                {aperturas
                  .filter(({ total }) => total > 0)
                  .map(
                    ({
                      negocio,
                      negocio_id,
                      total,
                      conteo,
                      aperturasTotal,
                      aperturasDetalle,
                    }) => {
                      return (
                        <>
                          <div className="col" key={negocio_id}>
                            <ChartContainer
                              title={
                                loading ? (
                                  <Spinner
                                    className="bg-primary"
                                    animation="grow"
                                  />
                                ) : (
                                  total + ` solicitudes de ${negocio}`
                                )
                              }
                            >
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={conteo}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    paddingAngle={5}
                                    dataKey="value" // Ahora se refiere a "total"
                                    nameKey="name" // Ahora se refiere a "negocio"
                                  >
                                    {conteo.map((entry, index) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={COLORS[index % COLORS.length]} // Usar color del mapa o color predeterminado
                                      />
                                    ))}
                                  </Pie>
                                  <Tooltip />
                                  <Legend />
                                </PieChart>
                              </ResponsiveContainer>
                            </ChartContainer>
                          </div>
                          <div className="col" key={negocio_id}>
                            <div className="position-relative">
                              <div className="position-absolute top-0 end-0">
                                <Button
                                  variant="outline-danger"
                                  onClick={() => {
                                    denegadas
                                      ? setDenegadas(false)
                                      : setDenegadas(true);
                                  }}
                                >
                                  {denegadas ? "Ver denegadas" : "Ver detalle"}
                                </Button>
                              </div>
                            </div>
                            <ChartContainer
                              title={
                                loading ? (
                                  <Spinner
                                    className="bg-primary"
                                    animation="grow"
                                  />
                                ) : (
                                  conteo.find(
                                    (item) => item.name === "Denegado"
                                  )?.value + ` solicitudes denegadas ${negocio}`
                                )
                              }
                            >
                              {denegadas ? (
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie
                                      data={aperturasTotal}
                                      cx="50%"
                                      cy="50%"
                                      innerRadius={60}
                                      outerRadius={100}
                                      fill="#8884d8"
                                      paddingAngle={5}
                                      dataKey="value" // Ahora se refiere a "total"
                                      nameKey="name" // Ahora se refiere a "negocio"
                                    >
                                      {aperturasTotal.map((entry, index) => (
                                        <Cell
                                          key={`cell-${index}`}
                                          fill={COLORS[index % COLORS.length]} // Usar color del mapa o color predeterminado
                                        />
                                      ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                  </PieChart>
                                </ResponsiveContainer>
                              ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    width={500}
                                    height={300}
                                    data={aperturasDetalle}
                                    margin={{
                                      top: 5,
                                      right: 30,
                                      left: 20,
                                      bottom: 5,
                                    }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="value" fill="#d31d1d" />
                                  </BarChart>
                                </ResponsiveContainer>
                              )}
                            </ChartContainer>
                          </div>
                        </>
                      );
                    }
                  )}
              </div>
              <br />
              <div className="row mt-2">
                {aperturas
                  .filter(({ total }) => total > 0)
                  .map(
                    ({
                      resultado,
                      negocio,
                      negocio_id,
                      participacion,
                      usoBot,
                    }) => {
                      return (
                        <>
                          <div className="col-8" key={negocio_id}>
                            <div className="position-relative">
                              <div className="position-absolute top-0 end-0">
                                <Button
                                  variant="outline-primary"
                                  onClick={() => {
                                    barras ? setBarras(false) : setBarras(true);
                                  }}
                                >
                                  {barras
                                    ? "Cambiar a horizontal"
                                    : "Cambiar a vertical"}
                                </Button>
                              </div>
                            </div>

                            <ChartContainer
                              title={`Solicitudes de Aperturas por cedis de ${negocio}`}
                            >
                              {barras ? (
                                <ResponsiveContainer width="100%" height={400}>
                                  <BarChart
                                    width={500}
                                    height={300}
                                    data={resultado}
                                    margin={{
                                      top: 5,
                                      right: 30,
                                      left: 20,
                                      bottom: 5,
                                    }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="value" fill="#8884d8" />
                                  </BarChart>
                                </ResponsiveContainer>
                              ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    layout="vertical"
                                    width={500}
                                    height={300}
                                    data={resultado}
                                    margin={{
                                      top: 5,
                                      right: 30,
                                      left: 20,
                                      bottom: 5,
                                    }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis dataKey="name" type="category" />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="value" fill="#82ca9d" />
                                  </BarChart>
                                </ResponsiveContainer>
                              )}
                            </ChartContainer>
                          </div>
                          <div className="col-4">
                            <h4 className="text-center">
                              Uso del BOT en Centros {negocio}
                            </h4>
                            {valoresCedis(participacion)}
                            <p className="row text-center">
                              <div className="col-6">
                                <span style={circleStyle("green")}></span>En uso{" "}
                                <b>{usoBot.uso}</b>
                              </div>
                              <div className="col-6">
                                <span style={circleStyle("red")}></span>No se
                                usa <b>{usoBot.desuso}</b>
                              </div>
                            </p>
                          </div>
                        </>
                      );
                    }
                  )}
              </div>
            </div>
          ) : (
            <h4 className="text-center pt-4">
              No se han registrado aperturas para este negocio.
            </h4>
          )}
        </Tab>
      </Tabs>
    </>
  );
};
